import { ChainId } from '@pancakeswap/sdk'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { gql, GraphQLClient } from 'graphql-request'
import { ChartDayData } from '../../types'
import { fetchDerivedProtocolTVLHistory } from './derived'

// format dayjs with the libraries that we need
dayjs.extend(utc)
dayjs.extend(weekOfYear)
const ONE_DAY_UNIX = 24 * 60 * 60

// Helper function to check if chainId is Kasplex
const isKasplexChain = (chainId: ChainId): boolean => {
  return chainId === ChainId.KASPLEX_TESTNET || chainId === ChainId.KASPLEX_MAINNET
}

// Helper function to fetch historical KAS prices
interface KasPriceData {
  date: string
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
  marketCap: number
  circulatingSupply: number
}

interface KasPriceApiResponse {
  success: boolean
  ok: boolean
  data: {
    symbol: string
    name: string
    count: number
    dateRange: {
      start: string
      end: string
    }
    prices: KasPriceData[]
  }
  timestamp: string
}

async function fetchHistoricalKasPrices(startDate: number, endDate: number): Promise<Map<string, number>> {
  const priceMap = new Map<string, number>()

  try {
    const apiUrl = process.env.NEXT_PUBLIC_KASPA_PRICE_API
    if (!apiUrl) {
      console.warn('NEXT_PUBLIC_KASPA_PRICE_API is not configured')
      return priceMap
    }

    // Calculate days difference for the API
    const daysDiff = Math.ceil((endDate - startDate) / ONE_DAY_UNIX)

    const response = await fetch(`${apiUrl}/historical/kas?days=${daysDiff}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch KAS prices: ${response.statusText}`)
    }

    const apiResponse: KasPriceApiResponse = await response.json()

    if (!apiResponse.success || !apiResponse.data?.prices) {
      console.error('Invalid KAS price API response structure')
      return priceMap
    }

    // Create a map of date (YYYY-MM-DD) to closing price
    apiResponse.data.prices.forEach((item) => {
      const dateKey = dayjs(item.date).format('YYYY-MM-DD')
      priceMap.set(dateKey, item.close)
    })
  } catch (error) {
    console.error('Error fetching historical KAS prices:', error)
  }

  return priceMap
}

const GLOBAL_CHART = gql`
  query pancakeDayDatas($startTime: Int!, $skip: Int!) {
    pancakeDayDatas(
      first: 1000
      skip: $skip
      where: { date_gt: $startTime }
      orderBy: date
      orderDirection: asc
      block: { number: 17189040 }
    ) {
      id
      date
      volumeUSD
      volumeETH
      tvlUSD
      tvlETH
    }
  }
`

interface ChartResults {
  pancakeDayDatas: {
    date: number
    volumeUSD: string
    volumeETH: string
    tvlUSD: string
    tvlETH: string
  }[]
}

export async function fetchChartData(client: GraphQLClient, chainId?: ChainId) {
  let data: {
    date: number
    volumeUSD: string
    volumeETH: string
    tvlUSD: string
    tvlETH: string
  }[] = []
  const startTimestamp = 1619170975
  const endTimestamp = dayjs.utc().unix()

  let error = false
  const skip = 0

  try {
    const chartData = await client.request<ChartResults>(GLOBAL_CHART, {
      startTime: startTimestamp,
      skip,
    })
    data = chartData.pancakeDayDatas
  } catch (err) {
    error = true
    console.error('Error fetching chart data:', err)
  }

  if (data && data.length > 0) {
    // Fetch historical KAS prices if chainId is Kasplex and we have data
    let kasPriceMap = new Map<string, number>()
    const shouldFetchKasPrices = chainId && isKasplexChain(chainId) && data.length > 0

    if (shouldFetchKasPrices) {
      const firstDate = data[0]?.date || startTimestamp
      kasPriceMap = await fetchHistoricalKasPrices(firstDate, endTimestamp)
    }

    const formattedExisting = data.reduce((accum: { [date: number]: ChartDayData }, dayData) => {
      const roundedDate = parseInt((dayData.date / ONE_DAY_UNIX).toFixed(0))

      let volumeUSD = parseFloat(dayData.volumeUSD)
      const volumeETH = parseFloat(dayData.volumeETH)

      // Calculate volumeUSD from volumeETH if volumeUSD is 0 or missing but volumeETH exists
      if (shouldFetchKasPrices && (!volumeUSD || volumeUSD === 0) && volumeETH > 0) {
        const dateKey = dayjs.unix(dayData.date).format('YYYY-MM-DD')
        const kasPrice = kasPriceMap.get(dateKey)

        if (kasPrice) {
          volumeUSD = volumeETH * kasPrice
        }
      }

      let tvlUSD = parseFloat(dayData.tvlUSD)
      const tvlETH = parseFloat(dayData.tvlETH)

      // Calculate tvlUSD from tvlETH if tvlUSD is 0 or missing but tvlETH exists
      if (shouldFetchKasPrices && (!tvlUSD || tvlUSD === 0) && tvlETH > 0) {
        const dateKey = dayjs.unix(dayData.date).format('YYYY-MM-DD')
        const kasPrice = kasPriceMap.get(dateKey)

        if (kasPrice) {
          tvlUSD = tvlETH * kasPrice
        }
      }

      // eslint-disable-next-line no-param-reassign
      accum[roundedDate] = {
        date: dayData.date,
        volumeUSD,
        volumeETH,
        tvlUSD,
      }
      return accum
    }, {})

    const firstEntry = formattedExisting[parseInt(Object.keys(formattedExisting)[0])]

    // fill in empty days ( there will be no day datas if no trades made that day )
    let timestamp = firstEntry?.date ?? startTimestamp
    let latestTvl = firstEntry?.tvlUSD ?? 0
    while (timestamp < endTimestamp - ONE_DAY_UNIX) {
      const nextDay = timestamp + ONE_DAY_UNIX
      const currentDayIndex = parseInt((nextDay / ONE_DAY_UNIX).toFixed(0))
      if (!Object.keys(formattedExisting).includes(currentDayIndex.toString())) {
        formattedExisting[currentDayIndex] = {
          date: nextDay,
          volumeUSD: 0,
          volumeETH: 0,
          tvlUSD: latestTvl,
        }
      } else {
        latestTvl = formattedExisting[currentDayIndex].tvlUSD
      }
      timestamp = nextDay
    }

    return {
      data: Object.values(formattedExisting),
      error: false,
    }
  }
  return {
    data: undefined,
    error,
  }
}

/**
 * Fetch historic chart data
 */

export async function fetchGlobalChartData(
  dataClient: GraphQLClient,
  chainId: ChainId,
): Promise<{
  error: boolean
  data: ChartDayData[] | undefined
}> {
  try {
    const derivedData = await fetchDerivedProtocolTVLHistory(dataClient, chainId)
    const { data } = await fetchChartData(dataClient, chainId)

    const shouldUserDerivedData = chainId === ChainId.ETHEREUM

    // @TODO: remove this once we have fix for mainnet TVL issue
    const formattedData = shouldUserDerivedData ? derivedData : data

    return {
      error: false,
      data: formattedData,
    }
  } catch (e) {
    console.error(e)
    return {
      error: true,
      data: undefined,
    }
  }
}
